/**
 * Question-shaped landing pages for homeowner search intent.
 *
 * These answer the sentence-style queries people actually type into search engines,
 * then cross-link into the glossary, state-law pages, and the property audit tool.
 */

import { PAA_QUESTIONS } from "./homeowner-questions-paa";

export type HomeownerQuestion = {
  slug: string;
  question: string;
  shortAnswer: string;
  /** Search-result title (max 64 chars) written to match how people phrase the query; falls back to the question. */
  seoTitle?: string;
  /** Search-result description (max 160 chars); falls back to the short answer. */
  seoDescription?: string;
  answer: string[];
  whatToLookFor?: string[];
  stateLawNote?: string;
  relatedGlossary: string[];
  relatedQuestions: string[];
  category: QuestionCategory;
  /** County record hubs that fit this question, each with a note on why. */
  countyHubs?: { hubId: string; note: string }[];
};

export type QuestionCategory =
  | "Building & fences"
  | "Driveways & access"
  | "Finding easements"
  | "Types of easements"
  | "Disputes & neighbors"
  | "Buying & selling"
  | "Utility requests"
  | "Getting an easement";

export const QUESTION_CATEGORIES: QuestionCategory[] = [
  "Building & fences",
  "Driveways & access",
  "Finding easements",
  "Types of easements",
  "Disputes & neighbors",
  "Buying & selling",
  "Utility requests",
  "Getting an easement",
];

const BASE_QUESTIONS: HomeownerQuestion[] = [
  {
    slug: "can-my-neighbor-use-my-driveway",
    question: "Can my neighbor use my driveway?",
    shortAnswer:
      "Only if a recorded easement or driveway agreement gives them the right. A long-standing habit, by itself, does not create a legal right to use your driveway.",
    answer: [
      "A neighbor can legally use your driveway only if the right is recorded in the public land records. The most common forms are an appurtenant easement (the right runs with the land), an easement by necessity (their lot has no other access), or a written driveway maintenance agreement.",
      "If your neighbor has simply been parking in or crossing your driveway for years, that use could ripen into a prescriptive easement in some states. The required period of open, continuous, and unauthorized use varies from about 5 to 20 years depending on state law.",
      "Before you buy a property with a shared or disputed driveway, check the title commitment for any recorded easement and compare the recorded width and location to what you see on the ground. If the documents and the physical use do not match, resolve the discrepancy before closing.",
    ],
    whatToLookFor: [
      "A recorded easement or access agreement in the deed, plat, or title commitment.",
      "The exact width, location, and permitted uses (passenger vehicles only, or commercial traffic too).",
      "Maintenance and cost-sharing clauses for gravel, asphalt, snow removal, or gate repairs.",
      "Whether the neighbor's lot is landlocked without the driveway, this can create an easement by necessity.",
    ],
    relatedGlossary: ["appurtenant-easement", "easement-by-necessity", "prescriptive-easement", "right-of-way"],
    relatedQuestions: ["what-is-an-easement-on-my-property", "how-to-find-easements-on-my-property"],
    category: "Driveways & access",
  },
  {
    slug: "what-is-a-utility-easement-on-my-property",
    question: "What is a utility easement on my property?",
    shortAnswer:
      "A utility easement gives a power, water, gas, telecom, or sewer company the legal right to enter, use, and maintain a defined strip of your land for infrastructure.",
    answer: [
      "Utility easements are the most common type of easement on residential lots. They let public and private utilities install, inspect, repair, and replace lines, poles, transformers, manholes, and other equipment. The easement is usually recorded on the plat or in a separate deed.",
      "The easement typically limits what you can build, plant, or store inside the corridor. Structures, permanent fences, deep-rooted trees, and pools are often prohibited because they interfere with maintenance access.",
      "Some utility easements are blanket easements, meaning they cover the entire parcel until the utility actually locates facilities. Blanket easements can be hard to spot on a survey because there may be no visible corridor.",
    ],
    whatToLookFor: [
      "The recorded width and location on the plat or in a utility deed.",
      "Whether the mapped corridor matches the location of poles, pedestals, transformers, or manholes.",
      "Blanket easements that encumber the whole parcel until facilities are built.",
      "Maintenance clauses that allow crews to clear vegetation or remove obstructions.",
    ],
    relatedGlossary: ["utility-easement", "easement", "right-of-way", "blanket-easement"],
    relatedQuestions: ["can-i-build-a-shed-over-an-easement", "how-to-find-easements-on-my-property"],
    category: "Types of easements",
  },
  {
    slug: "how-to-find-easements-on-my-property",
    seoTitle: "How to Find Easements on Your Property: 4 Free Ways",
    seoDescription:
      "Find recorded easements on your lot: check Schedule B of your title commitment, search the county recorder, open the county GIS viewer and pull your plat.",
    question: "How do I find easements on my property?",
    shortAnswer:
      "Start with your title commitment or preliminary title report, then check the county recorder, GIS parcel viewer, and any utility or homeowner association documents.",
    answer: [
      "The easiest place to find recorded easements is your title commitment, specifically Schedule B. This section lists exceptions to title coverage, including easements, covenants, and rights-of-way that affect the property.",
      "Next, search the county recorder's office or online land records for your parcel. Look for deeds, plats, easement deeds, and right-of-way agreements that mention your lot or block.",
      "Many counties publish a GIS parcel viewer that overlays tax parcels with recorded easements and corridor layers. These viewers are free, but the quality varies by county. Some show precise vector lines; others show only approximate locations.",
      "Finally, check with your homeowner association, utility companies, and any neighbors who share driveways or access paths. Unrecorded or informal agreements can still create practical problems even if they are not legally enforceable.",
    ],
    whatToLookFor: [
      "Schedule B of your title commitment or preliminary title report.",
      "Deed, plat, and easement records at the county recorder or clerk.",
      "County GIS parcel viewer with easement or right-of-way layers.",
      "HOA covenants and utility correspondence.",
    ],
    relatedGlossary: ["easement", "title-commitment", "schedule-b", "right-of-way"],
    relatedQuestions: ["what-is-an-easement-on-my-property", "can-i-build-a-shed-over-an-easement"],
    category: "Finding easements",
  },
  {
    slug: "can-i-build-a-shed-over-an-easement",
    seoTitle: "Can You Build a Shed Over an Easement? Risks & Removal Rules",
    seoDescription:
      "Usually no. If a shed blocks access, the holder can often remove it at your cost. See when movable sheds are tolerated and how to get written approval first.",
    question: "Can I build a shed over an easement?",
    shortAnswer:
      "Usually no. Building over a utility or access easement typically violates the easement terms and the structure may have to be removed at your expense.",
    answer: [
      "Most easement documents prohibit permanent structures inside the easement corridor. The reason is simple: the easement holder needs unobstructed access to maintain, repair, or replace its facilities. A shed, garage, fence, or pool can block that access.",
      "If you build over a utility easement, the utility may remove the structure without compensating you. Some easements also give the utility the right to enter your property without advance notice for emergencies.",
      "There are limited exceptions. Some narrow maintenance easements allow surface improvements if they do not interfere with access. Always get written approval from the easement holder and record any variance before you build.",
    ],
    whatToLookFor: [
      "The recorded easement width and any explicit prohibition on structures.",
      "Maintenance clauses that allow the easement holder to remove obstructions.",
      "Setback rules from the easement edge in local zoning or HOA covenants.",
      "Written permission from the easement holder before building.",
    ],
    relatedGlossary: ["utility-easement", "easement", "encroachment", "setback"],
    relatedQuestions: ["what-is-a-utility-easement-on-my-property", "how-close-to-property-line-can-i-build-a-fence"],
    category: "Building & fences",
  },
  {
    slug: "easement-vs-right-of-way",
    question: "Easement vs right-of-way: what is the difference?",
    shortAnswer:
      "A right-of-way is a type of easement that lets someone pass over your land. An easement is the broader legal right to use part of your property for a specific purpose.",
    answer: [
      "An easement is any legal right to use another person's land for a limited purpose. Easements can cover utilities, conservation, access, drainage, views, light, and many other uses.",
      "A right-of-way is an easement whose purpose is travel. It gives the holder the right to pass across the land, usually by foot or vehicle. Common examples include shared driveways, private roads, and public sidewalks.",
      "In everyday conversation the terms are often swapped, but in title work the distinction matters. A utility easement is not a right-of-way if it only allows equipment maintenance, while a driveway easement is a right-of-way because it allows passage.",
    ],
    whatToLookFor: [
      "The purpose clause in the recorded easement document.",
      "Whether the right includes passage or only maintenance access.",
      "The width and surface requirements for any travel corridor.",
      "Who pays for upkeep if the right-of-way is shared.",
    ],
    relatedGlossary: ["easement", "right-of-way", "appurtenant-easement", "easement-by-necessity"],
    relatedQuestions: ["can-my-neighbor-use-my-driveway", "what-is-a-utility-easement-on-my-property"],
    category: "Types of easements",
  },
  {
    slug: "how-close-to-property-line-can-i-build-a-fence",
    question: "How close to the property line can I build a fence?",
    shortAnswer:
      "It depends on local zoning, HOA rules, and any recorded easements. Many cities require a setback of a few feet, and fences inside an easement may be removed.",
    answer: [
      "Fence setback rules are set by local zoning ordinances and homeowner associations, not by state law. A common residential rule is a 2- to 3-foot setback from the side and rear property lines, but the exact distance varies by jurisdiction.",
      "If your property has a utility or access easement along the boundary, placing a fence inside the easement corridor can violate the easement terms. The easement holder may remove the fence to reach its facilities.",
      "Before you build, locate the actual property line. Do not rely on existing fences, hedges, or neighbor statements. A licensed surveyor can mark the boundary and identify any easements that affect the fence location.",
    ],
    whatToLookFor: [
      "Local zoning setback requirements for fences.",
      "HOA design guidelines and fence restrictions.",
      "Recorded easements along the property line.",
      "A current survey that marks the true boundary.",
    ],
    relatedGlossary: ["setback", "encroachment", "easement", "survey"],
    relatedQuestions: ["can-i-build-a-shed-over-an-easement", "what-is-an-easement-on-my-property"],
    category: "Building & fences",
  },
  {
    slug: "what-is-an-easement-on-my-property",
    question: "What is an easement on my property?",
    shortAnswer:
      "An easement is a legal right for someone else to use a specific part of your land for a specific purpose, such as access, utilities, or drainage.",
    answer: [
      "An easement gives another person or entity a limited right to use your property without owning it. The right is usually recorded in the public land records and transfers to future owners when the property is sold.",
      "Common residential easements include utility easements for power and sewer lines, driveway easements for shared access, and drainage easements for stormwater. Less common types include conservation easements and prescriptive easements created by long use.",
      "An easement can reduce how you use the affected part of your lot. You may not be able to build, fence, or landscape inside the easement corridor. The easement holder can also enter the property to maintain its rights.",
    ],
    whatToLookFor: [
      "Recorded easement documents in your deed or title commitment.",
      "The specific purpose, width, and location of each easement.",
      "Whether the easement is appurtenant (runs with the land) or in gross (personal to a utility or individual).",
      "Any maintenance, access, or removal clauses.",
    ],
    relatedGlossary: ["easement", "appurtenant-easement", "easement-in-gross", "prescriptive-easement"],
    relatedQuestions: ["how-to-find-easements-on-my-property", "easement-vs-right-of-way"],
    category: "Types of easements",
  },
  {
    slug: "can-i-remove-an-easement-from-my-property",
    question: "Can I remove an easement from my property?",
    shortAnswer:
      "Sometimes. Easements can be removed by agreement, abandonment, merger, or court order, but the process is usually slow and may require all benefited parties to consent.",
    answer: [
      "The simplest way to remove an easement is by written release from the easement holder. Both the benefited and burdened property owners typically sign a release deed, which is then recorded in the land records.",
      "An easement may also end through abandonment if the holder stops using it and takes affirmative steps showing intent to give it up. Mere non-use is usually not enough; there must be evidence of intent to abandon.",
      "If the dominant and servient estates are combined into one owner, the easement can end by merger. In some cases, a court can terminate an easement that is no longer needed or was obtained by prescription.",
      "Removing an easement without the holder's consent can be expensive and uncertain. Consult a real estate attorney before attempting to block, relocate, or terminate a recorded easement.",
    ],
    whatToLookFor: [
      "The recorded easement document and the identity of the easement holder.",
      "Evidence of abandonment or non-use.",
      "Whether the benefited and burdened parcels are now under common ownership.",
      "A signed release deed prepared by an attorney.",
    ],
    relatedGlossary: ["easement", "appurtenant-easement", "easement-in-gross", "prescriptive-easement"],
    relatedQuestions: ["can-my-neighbor-use-my-driveway", "what-is-a-prescriptive-easement"],
    category: "Disputes & neighbors",
  },
  {
    slug: "what-is-a-prescriptive-easement",
    question: "What is a prescriptive easement?",
    shortAnswer:
      "A prescriptive easement is a right to use someone else's land that is created by open, continuous, and unauthorized use for the period required by state law.",
    answer: [
      "Unlike a written easement, a prescriptive easement arises from use. If a person uses part of a neighbor's property openly, continuously, and without permission for the statutory period, they may gain a legal right to continue that use.",
      "The required period varies widely by state, commonly from 5 to 20 years. Some states also require the use to be hostile, adverse, exclusive, or under a claim of right. The exact elements depend on state law.",
      "Prescriptive easements are a frequent source of boundary disputes. A neighbor's fence, driveway, path, or garden that crosses onto your lot could mature into a prescriptive easement if it meets the statutory test and the true owner does not stop it in time.",
    ],
    whatToLookFor: [
      "Long-standing uses that do not match the surveyed boundary.",
      "The statutory prescriptive period for your state.",
      "Whether the use was open, continuous, and without permission.",
      "Any written permission given to the neighbor, which can defeat a prescriptive claim.",
    ],
    stateLawNote: "prescriptive",
    relatedGlossary: ["prescriptive-easement", "adverse-possession", "encroachment", "easement"],
    relatedQuestions: ["can-my-neighbor-use-my-driveway", "can-i-remove-an-easement-from-my-property"],
    category: "Types of easements",
  },
  {
    slug: "who-maintains-a-shared-driveway",
    question: "Who maintains a shared driveway?",
    shortAnswer:
      "Maintenance responsibility is usually set by a recorded driveway agreement. Without one, state law may split costs based on use or benefit, but disputes are common.",
    answer: [
      "A well-drafted shared driveway agreement records each owner's share of maintenance costs, including gravel, asphalt, snow removal, and drainage. It also sets rules for parking, access, and improvements.",
      "If there is no recorded agreement, the owners may share costs in proportion to their use, or the burdened owner may be solely responsible depending on state law. These disputes often end up in court because the default rules are vague.",
      "Before buying a property with a shared driveway, ask for the recorded maintenance agreement and confirm it is current. If no agreement exists, consider negotiating one before closing.",
    ],
    whatToLookFor: [
      "A recorded shared driveway or maintenance agreement.",
      "Cost-sharing percentages for repairs and snow removal.",
      "Rules for parking, turning around, and guest access.",
      "Procedures for approving resurfacing or major repairs.",
    ],
    relatedGlossary: ["appurtenant-easement", "easement-by-necessity", "right-of-way"],
    relatedQuestions: ["can-my-neighbor-use-my-driveway", "easement-vs-right-of-way"],
    category: "Driveways & access",
  },
  {
    slug: "what-is-an-easement-by-necessity",
    question: "What is an easement by necessity?",
    shortAnswer:
      "An easement by necessity gives a landlocked parcel legal access across a neighboring parcel when no other reasonable access exists.",
    answer: [
      "When a parcel is completely surrounded by other private land and has no public road frontage, the law may imply an easement by necessity over the most reasonable route to a public road. The easement is usually the shortest and least damaging path.",
      "This type of easement is implied by law, not created by a written document. It typically requires strict necessity at the time the parcels were separated from common ownership, though some states relax this standard.",
      "An easement by necessity can be lost if the dominant parcel gains another access route. It is also narrower than many written easements and may allow only ingress and egress, not parking or utilities.",
    ],
    whatToLookFor: [
      "Whether the parcel has any public road frontage.",
      "The common ownership history of the dominant and servient parcels.",
      "The most reasonable route to a public road.",
      "Whether the necessity still exists or has been eliminated by a new access route.",
    ],
    relatedGlossary: ["easement-by-necessity", "appurtenant-easement", "right-of-way"],
    relatedQuestions: ["can-my-neighbor-use-my-driveway", "who-maintains-a-shared-driveway"],
    category: "Types of easements",
  },
  {
    slug: "how-do-utility-easements-affect-property-value",
    question: "How do utility easements affect property value?",
    shortAnswer:
      "Utility easements can reduce usable yard space, limit building options, and affect resale appeal, but they usually do not make a property unmarketable.",
    answer: [
      "A utility easement reduces the area where you can build, landscape, or plant trees. Buyers may view this as a negative if the easement crosses a prime building site or creates an unsightly corridor with poles or equipment.",
      "However, most residential utility easements are along rear or side lot lines and have minimal impact on daily use. They are so common that buyers and lenders usually accept them as a normal part of suburban development.",
      "The effect on value depends on the easement's location, width, visibility, and the restrictions it imposes. A narrow rear utility strip is rarely a deal-breaker; a wide overhead power line corridor across the backyard can be.",
    ],
    whatToLookFor: [
      "The width and location of the easement on the plat.",
      "Whether poles, transformers, or manholes are visible in the yard.",
      "Restrictions on structures, pools, and trees inside the corridor.",
      "How the easement affects the usable building envelope of the lot.",
    ],
    relatedGlossary: ["utility-easement", "easement", "encroachment"],
    relatedQuestions: ["what-is-a-utility-easement-on-my-property", "can-i-build-a-shed-over-an-easement"],
    category: "Types of easements",
  },
  {
    slug: "can-i-plant-trees-in-a-utility-easement",
    seoTitle: "Can You Plant Trees in a Utility Easement? Rules & Clearance",
    seoDescription:
      "Usually no. Utilities can cut or remove trees inside an easement without paying you. See typical clearances, plants usually allowed, and who to call first.",
    question: "Can I plant trees in a utility easement?",
    shortAnswer:
      "Usually no. Most utility easements prohibit trees and large shrubs because they interfere with power lines and underground facilities.",
    answer: [
      "Utility easements typically include vegetation clauses that allow the easement holder to trim or remove trees, shrubs, and other plants inside the corridor. This is especially strict for overhead electric lines and underground gas or fiber lines.",
      "Some easements allow low-growing ground cover or grasses, but anything with deep roots or significant height is usually prohibited. If you plant a prohibited tree, the utility may remove it without compensating you.",
      "Always read the recorded easement or call the utility before planting. A quick records check can save you the cost and disappointment of losing mature landscaping.",
    ],
    whatToLookFor: [
      "The vegetation clause in the recorded easement.",
      "Whether the easement covers overhead lines, underground lines, or both.",
      "Any approved list of low-growing plants.",
      "The utility's right to remove unapproved vegetation without notice.",
    ],
    relatedGlossary: ["utility-easement", "easement", "blanket-easement"],
    relatedQuestions: ["what-is-a-utility-easement-on-my-property", "can-i-build-a-shed-over-an-easement"],
    category: "Building & fences",
  },
  {
    slug: "what-is-a-conservation-easement",
    question: "What is a conservation easement?",
    shortAnswer:
      "A conservation easement is a permanent legal agreement that limits development on a property to protect natural, scenic, or historic values.",
    answer: [
      "Conservation easements are voluntary agreements between a landowner and a qualified organization, usually a land trust or government agency. The landowner keeps ownership but gives up certain development rights forever.",
      "These easements can provide significant tax benefits, including federal income tax deductions and estate tax reductions. The value of the deduction is generally based on the difference in value before and after the restriction.",
      "Because conservation easements run with the land forever, they bind all future owners. Before buying a property with a conservation easement, review the exact restrictions and the easement holder's enforcement rights.",
    ],
    whatToLookFor: [
      "The conservation easement deed and any approved uses.",
      "The easement holder's monitoring and enforcement rights.",
      "Tax benefit documentation if the easement was donated.",
      "Any reserved rights such as limited building envelopes or agricultural use.",
    ],
    relatedGlossary: ["conservation-easement", "easement", "easement-in-gross"],
    relatedQuestions: ["what-is-an-easement-on-my-property", "can-i-remove-an-easement-from-my-property"],
    category: "Types of easements",
  },
  {
    slug: "how-to-read-a-title-commitment-for-easements",
    question: "How do I read a title commitment for easements?",
    shortAnswer:
      "Focus on Schedule B of the title commitment. This section lists every recorded exception, including easements, covenants, and rights-of-way that affect the property.",
    answer: [
      "A title commitment is the title company's promise to issue a policy. Schedule A identifies the property and proposed insured. Schedule B lists the exceptions the policy will not cover, and that is where easements live.",
      "Each Schedule B item should reference a recorded document, such as a deed book and page number or an instrument number. Use those references to pull the actual easement document from the county recorder.",
      "Do not rely on the title commitment alone. The commitment summarizes the exception; the underlying document defines the easement's width, location, purpose, and restrictions. Read both together.",
    ],
    whatToLookFor: [
      "Schedule B exceptions labeled as easements, rights-of-way, or covenants.",
      "Recorded document references for each exception.",
      "The legal description and map or exhibit attached to the easement.",
      "Any blanket or unlocated easements that affect the whole parcel.",
    ],
    relatedGlossary: ["title-commitment", "schedule-b", "title-policy-exception", "easement"],
    relatedQuestions: ["how-to-find-easements-on-my-property", "what-is-an-easement-on-my-property"],
    category: "Finding easements",
  },
  {
    slug: "what-is-encroachment",
    question: "What is encroachment on a property?",
    shortAnswer:
      "Encroachment is when a structure, fence, driveway, or landscaping crosses the legal boundary onto a neighboring property without permission.",
    answer: [
      "Encroachment is a physical invasion of another person's land. Common examples include a fence built a foot over the line, a shed that straddles the boundary, or a driveway that extends onto the neighbor's lot.",
      "An encroachment is not the same as an easement. An easement is a recorded right to use part of a property. An encroachment is an unauthorized use that may become a prescriptive easement or adverse possession claim over time.",
      "Encroachments are best resolved before closing. A survey can reveal them, and a title policy may or may not cover them depending on whether they are recorded or visible.",
    ],
    whatToLookFor: [
      "A current survey that compares fences and structures to the legal boundary.",
      "Recorded easements or agreements that might authorize the overlap.",
      "Signs of long-standing use that could ripen into a prescriptive claim.",
      "Neighbor correspondence about boundary disputes or encroachments.",
    ],
    relatedGlossary: ["encroachment", "prescriptive-easement", "adverse-possession", "survey"],
    relatedQuestions: ["what-is-a-prescriptive-easement", "can-i-remove-an-easement-from-my-property"],
    category: "Disputes & neighbors",
  },
  {
    slug: "do-easements-transfer-with-the-property",
    question: "Do easements transfer with the property?",
    shortAnswer:
      "Appurtenant easements usually transfer automatically with the land. Easements in gross may or may not transfer, depending on the document and state law.",
    answer: [
      "An appurtenant easement benefits a specific parcel and burdens another specific parcel. It runs with the land, meaning it transfers to every future owner of both parcels automatically.",
      "An easement in gross benefits a person or entity, such as a utility company. Commercial easements in gross are usually transferable. Personal easements in gross, such as a neighbor's right to fish in your pond, may not be.",
      "When you buy a property, the title commitment should disclose all recorded easements that transfer with it. Unrecorded easements may still be enforceable in some cases, but they are harder to discover and prove.",
    ],
    whatToLookFor: [
      "Whether the easement is described as appurtenant or in gross.",
      "The benefited and burdened parcels identified in the document.",
      "Any language about transferability or assignment.",
      "The title commitment's Schedule B exceptions.",
    ],
    relatedGlossary: ["appurtenant-easement", "easement-in-gross", "easement", "title-commitment"],
    relatedQuestions: ["what-is-an-easement-on-my-property", "can-i-remove-an-easement-from-my-property"],
    category: "Types of easements",
  },
  {
    slug: "can-a-neighbor-block-my-easement",
    question: "Can a neighbor block my easement?",
    shortAnswer:
      "No. Interfering with a recorded easement is generally illegal. The easement holder can sue to remove the obstruction and recover damages.",
    answer: [
      "A recorded easement gives the holder a legal right to use the burdened property for a specific purpose. The burdened owner cannot block, fence, or build in a way that prevents that use.",
      "If a neighbor blocks your easement, document the obstruction and review the recorded easement document. The first step is usually a demand letter. If that fails, you can file a lawsuit for easement interference or declaratory judgment.",
      "The burdened owner may be liable for damages and attorney fees in some states. However, the easement holder must stay within the scope of the easement; overuse can create a counterclaim.",
    ],
    whatToLookFor: [
      "The recorded easement document defining the scope of the right.",
      "Photos and dates of the obstruction.",
      "Written demands and neighbor responses.",
      "State law on easement interference and remedies.",
    ],
    relatedGlossary: ["easement", "appurtenant-easement", "easement-by-necessity", "right-of-way"],
    relatedQuestions: ["can-my-neighbor-use-my-driveway", "who-maintains-a-shared-driveway"],
    category: "Disputes & neighbors",
  },
  {
    slug: "what-is-a-drainage-easement",
    question: "What is a drainage easement?",
    shortAnswer:
      "A drainage easement gives a public or private party the right to direct stormwater across, through, or under part of your property.",
    answer: [
      "Drainage easements are common in subdivisions and urban areas where natural water flow has been altered by development. They let municipalities, developers, or neighbors manage stormwater through pipes, swales, ditches, or natural channels.",
      "These easements usually restrict what you can build inside the drainage corridor. You may not be allowed to fill the swale, install a pool, or construct a building that blocks the designed flow of water.",
      "Before buying a property with a drainage easement, check the recorded document for the easement's dimensions, permitted uses, and maintenance responsibilities. Flooding and drainage disputes can be expensive to fix after the fact.",
    ],
    whatToLookFor: [
      "The recorded drainage easement document and its dimensions.",
      "Whether the easement covers pipes, open swales, or natural channels.",
      "Maintenance responsibilities for clearing debris and vegetation.",
      "Restrictions on filling, fencing, or building inside the corridor.",
    ],
    relatedGlossary: ["easement", "utility-easement", "right-of-way"],
    relatedQuestions: ["what-is-a-utility-easement-on-my-property", "can-i-build-a-shed-over-an-easement"],
    category: "Types of easements",
  },
  {
    slug: "how-much-does-it-cost-to-survey-easements",
    question: "How much does it cost to survey easements on a property?",
    shortAnswer:
      "A residential boundary and easement survey typically costs a few hundred to a few thousand dollars, depending on lot size, terrain, and how many easements need to be located.",
    answer: [
      "A simple lot survey for a suburban home may cost a few hundred dollars. A more complex survey that locates multiple easements, compares recorded documents to physical features, and prepares an exhibit for legal use can cost more.",
      "The price depends on the property's size and shape, the number of easements, whether the surveyor must research old records, and how quickly you need the results. Rural, wooded, or irregular lots usually cost more.",
      "While a survey is an upfront cost, it is often far less expensive than a boundary dispute or a building setback violation. If you are unsure where an easement runs, a survey is the most reliable way to find out.",
    ],
    whatToLookFor: [
      "A licensed surveyor with experience in easement and boundary work.",
      "A clear scope of work that includes locating recorded easements.",
      "Comparison of the survey to the title commitment and recorded documents.",
      "Whether the surveyor will provide a map suitable for legal or permit use.",
    ],
    relatedGlossary: ["survey", "encroachment", "easement"],
    relatedQuestions: ["how-to-find-easements-on-my-property", "what-is-encroachment"],
    category: "Finding easements",
  },
  {
    slug: "what-are-the-three-types-of-easements",
    seoTitle: "3 Types of Easements: Appurtenant, In Gross, Prescriptive",
    seoDescription:
      "The three classic easements: appurtenant (tied to land), in gross (held by a person or company) and prescriptive (earned by years of use), with plain examples.",
    question: "What are the three types of easements?",
    shortAnswer:
      "The classic three are easements appurtenant, easements in gross, and prescriptive easements, one attaches to a neighboring parcel, one belongs to a company or person, and one is earned by years of use.",
    answer: [
      "An easement appurtenant benefits a specific parcel of land. The benefited lot is the dominant estate and the burdened lot is the servient estate, and because the right belongs to the land rather than the owner, it transfers automatically at every sale.",
      "An easement in gross belongs to a person or a company with no benefited parcel behind it. Utility corridors, pipelines, rail lines, billboards and cell sites are almost always in gross, and commercial versions of them can be bought and sold like any other business asset.",
      "A prescriptive easement is created by conduct rather than a signature. Open, continuous use of someone else's land without permission, for the period your state's statute sets, can ripen into a permanent right to keep using it. Some lists substitute easements by necessity, which courts imply when a parcel is landlocked by the split that created it.",
    ],
    whatToLookFor: [
      "Whether the grant names a benefited parcel. That is what makes it appurtenant rather than in gross.",
      "Whether the right was recorded at all; prescriptive and necessity easements usually are not.",
      "Language such as \"successors and assigns\" or \"runs with the land\", which signals permanence.",
      "The stated purpose, which limits what the holder may actually do.",
    ],
    relatedGlossary: ["appurtenant-easement", "easement-in-gross", "prescriptive-easement", "easement"],
    relatedQuestions: ["easement-vs-right-of-way", "what-is-an-easement-on-my-property"],
    category: "Types of easements",
  },
  {
    slug: "does-an-easement-lower-property-value",
    question: "Does an easement lower property value?",
    shortAnswer:
      "Sometimes. A narrow buried utility strip along a rear line often changes value very little, while a wide overhead corridor, a shared driveway, or a conservation restriction can move value materially.",
    answer: [
      "Appraisers do not apply a fixed percentage for easements. They value the property as if unencumbered, then value it as encumbered, and the difference is the effect. That before-and-after method is why two lots with the same easement width can show very different results.",
      "What drives the number is how much use the easement actually takes away. A corridor that crosses the only place a garage could go, or that removes subdivision potential, hurts. A strip under an existing lawn that nobody would ever build on hurts far less.",
      "Perception matters too, especially with overhead transmission lines, and it shows up as longer marketing time as much as a lower price. Local comparable sales that include similar corridors are the honest way to size that, rather than a rule of thumb from an internet forum.",
    ],
    whatToLookFor: [
      "How much of the buildable envelope the corridor removes, not just its width.",
      "Whether the easement blocks a use the market pays for: a shop, a pool, a second dwelling, a subdivision.",
      "Comparable sales in the same neighborhood that also carry the corridor.",
      "Restrictions on the surface, such as no fences or no trees, which affect livability.",
    ],
    relatedGlossary: ["encumbrance", "buildable-envelope", "utility-easement", "conservation-easement"],
    relatedQuestions: [
      "how-do-utility-easements-affect-property-value",
      "what-is-an-easement-on-my-property",
    ],
    category: "Types of easements",
  },
  {
    slug: "can-an-easement-be-moved",
    question: "Can an easement be moved to a different part of the property?",
    shortAnswer:
      "Traditionally only with the holder's written agreement, but a growing number of states now let the burdened owner relocate an easement at their own cost if the new route serves the holder just as well.",
    answer: [
      "Under the traditional rule, the location of an easement becomes fixed once it is described or established by use, and neither owner can change it unilaterally. That rule protects the holder from being pushed onto a worse route every time the burdened owner has a new plan.",
      "Many states have since adopted the modern approach reflected in the Restatement of Property, which allows the servient owner to relocate the easement if the change does not lessen its utility, increase the holder's burden or cost, or frustrate its purpose, with the servient owner paying for everything.",
      "The reliable path in either state is agreement: a recorded amendment describing the new route, signed by both owners, with the old route released in the same instrument. Doing the work first and asking afterward is how relocation projects turn into injunctions.",
    ],
    whatToLookFor: [
      "Whether the recorded grant already permits relocation, some do, explicitly.",
      "Your state's rule, since traditional and modern jurisdictions reach opposite outcomes.",
      "Whether the new route keeps the same width, grade and access quality.",
      "A recorded amendment plus a release of the old corridor, not just a handshake.",
    ],
    relatedGlossary: ["servient-estate", "dominant-estate", "right-of-way", "survey"],
    relatedQuestions: ["can-i-remove-an-easement-from-my-property", "can-a-neighbor-block-my-easement"],
    category: "Disputes & neighbors",
  },
  {
    slug: "can-i-build-a-fence-across-an-easement",
    question: "Can I build a fence across an easement?",
    shortAnswer:
      "Sometimes, if the fence does not materially interfere with the easement's purpose. But access easements, drainage swales and utility corridors each treat fences differently.",
    answer: [
      "For a utility easement, fences are often tolerated as long as crews can still reach the equipment. Many utilities require a removable panel or a gate, and most grants let them cut a fence to make a repair without any duty to rebuild it.",
      "For an access or driveway easement, a fence across the traveled way is interference, full stop. Even a gate is risky unless the holder gets a key or code and access is not delayed, and courts routinely order unilateral gates removed.",
      "For a drainage easement, the concern is water rather than access. Solid privacy fencing across a swale traps debris and changes the flow section, which is exactly what the plat note prohibits, so many jurisdictions require a gap at the bottom or refuse the permit.",
    ],
    whatToLookFor: [
      "What the recorded grant or plat note says about obstructions, in its own words.",
      "Whether the holder will issue written permission, get it before you buy materials.",
      "Local permit rules, which often require the easement to be shown on the fence permit drawing.",
      "Whether the fence line matches the surveyed easement line, not the mowed edge.",
    ],
    relatedGlossary: ["setback", "encroachment", "utility-easement", "right-of-way"],
    relatedQuestions: [
      "how-close-to-property-line-can-i-build-a-fence",
      "can-i-build-a-shed-over-an-easement",
    ],
    category: "Building & fences",
  },
  {
    slug: "what-happens-if-i-build-on-an-easement",
    question: "What happens if I build on an easement?",
    shortAnswer:
      "The holder can usually require the structure to be removed at your expense, and your title insurer generally will not cover the loss because the easement was disclosed as an exception.",
    answer: [
      "The first consequence is often administrative: the building department refuses the permit, or catches the encroachment at inspection. That is the cheap version, because nothing has been built yet.",
      "The expensive version arrives later. A utility needing access, or a neighbor whose driveway right you paved over, can seek an injunction. Courts weigh the hardship, but the general rule is that an encroaching structure inside a valid easement comes out, and the owner who built it pays.",
      "It also becomes a title problem you carry to closing. Buyers, lenders and insurers all flag structures inside easements, and clearing it usually means a recorded encroachment agreement from the holder or physically moving what you built.",
    ],
    whatToLookFor: [
      "The exact recorded corridor located by a surveyor before construction, not the plat scaled by eye.",
      "Whether the holder will sign an encroachment agreement, some will, for a fee and with removal conditions.",
      "Your title policy's Schedule B, which almost certainly lists the easement as an exception.",
      "Whether the structure is permanent or removable; decks on piers are treated differently from slabs.",
    ],
    relatedGlossary: ["encroachment", "buildable-envelope", "title-policy-exception", "schedule-b"],
    relatedQuestions: ["can-i-build-a-shed-over-an-easement", "what-is-encroachment"],
    category: "Building & fences",
  },
  {
    slug: "who-owns-the-land-under-an-easement",
    question: "Who owns the land under an easement?",
    shortAnswer:
      "You do. An easement transfers a right to use part of your land, not ownership of it. You keep title, you keep paying the tax, and you keep any use that does not interfere.",
    answer: [
      "This is the single most common misunderstanding about easements. The strip does not belong to the utility, the neighbor, or the city. It remains part of your parcel, counts toward your lot area for most zoning purposes, and passes with your deed when you sell.",
      "What the holder has is a limited right defined by the grant: to cross, to maintain lines, to move water. Anything outside that stated purpose is not theirs to do, which is why the purpose clause is worth reading carefully.",
      "Two exceptions cause confusion. A public road corridor may be held in fee by the agency rather than as an easement, in which case they really do own it. And an exclusive easement in gross can bar even the landowner from the corridor, which is close to ownership in practice if not in name.",
    ],
    whatToLookFor: [
      "Whether the instrument is an easement or a fee conveyance, the granting words differ.",
      "Whether the easement is described as exclusive, which restricts your own use.",
      "How the assessor treats the encumbered strip; some reduce value for heavy corridors.",
      "Your remaining rights: mowing, crossing, and shallow landscaping are usually still yours.",
    ],
    relatedGlossary: ["servient-estate", "easement", "encumbrance", "right-of-way"],
    relatedQuestions: ["what-is-an-easement-on-my-property", "do-easements-transfer-with-the-property"],
    category: "Types of easements",
  },
  {
    slug: "what-is-a-blanket-easement",
    question: "What is a blanket easement?",
    shortAnswer:
      "A blanket easement covers an entire parcel instead of a described strip, letting the holder place facilities anywhere on the property until a location is fixed by actual construction.",
    answer: [
      "Blanket easements are common in older rural grants, where a cooperative was given the right to build lines across a farm without anyone surveying a corridor first. The document names the whole parcel, not a width and centerline.",
      "In most states, once the holder actually installs facilities, the easement is treated as fixed at that location and the blanket effectively shrinks to the built corridor. That doctrine helps, but it is a rule of interpretation, not a recorded description, so the title exception still reads as a blanket.",
      "For a buyer or a lender, that is the practical problem: a blanket exception makes it hard to prove where you may safely build. The fix is a recorded amendment describing the actual corridor, which utilities will often sign once the lines are in and located.",
    ],
    whatToLookFor: [
      "Grant language that names the parcel but gives no width or route.",
      "Whether facilities have been built, which usually fixes the location by law.",
      "Whether the holder will record a corridor amendment before your closing.",
      "How your title company treats the exception, since it may limit coverage on future improvements.",
    ],
    relatedGlossary: ["blanket-easement", "utility-easement", "title-commitment", "schedule-b"],
    relatedQuestions: [
      "what-is-a-utility-easement-on-my-property",
      "how-to-read-a-title-commitment-for-easements",
    ],
    category: "Types of easements",
  },
  {
    slug: "how-do-i-find-out-who-owns-an-easement",
    question: "How do I find out who owns an easement on my property?",
    shortAnswer:
      "Start with the grantee named in the recorded document, then trace assignments and corporate successors, the company on a forty-year-old grant is frequently not the company that holds it today.",
    answer: [
      "The recorded instrument names the original grantee. Pull it by book and page from the county recorder, clerk, or register of deeds, and note the exact corporate name and the date.",
      "Then look for assignments. Utility and pipeline easements change hands through mergers and asset sales, and the recorded chain in your county may be incomplete because corporate mergers are often filed elsewhere. State corporation records and public utility commission filings fill the gaps.",
      "For an appurtenant easement, the holder is simply whoever owns the benefited parcel today, which the assessor's records will tell you. That is usually the fastest research task in easement work, and the one owners most often overcomplicate.",
    ],
    whatToLookFor: [
      "The grantee's exact legal name and any \"successors and assigns\" language.",
      "Recorded assignments, mergers or memoranda referencing the original book and page.",
      "For appurtenant rights, the current owner of the benefited parcel in assessor records.",
      "Utility commission or state corporation filings when the named company no longer exists.",
    ],
    relatedGlossary: ["easement-in-gross", "dominant-estate", "title-commitment", "encumbrance"],
    relatedQuestions: ["how-to-find-easements-on-my-property", "do-easements-transfer-with-the-property"],
    category: "Finding easements",
  },
  {
    slug: "can-an-easement-expire",
    question: "Can an easement expire?",
    shortAnswer:
      "Most easements are perpetual and do not expire on their own, but they can end through merger, release, abandonment, expiration of a stated term, or the end of the necessity that created them.",
    answer: [
      "Perpetual is the default. A typical utility or access grant says so explicitly, and even where it is silent, courts read a grant with no stated term as permanent unless something in it points the other way.",
      "The realistic endings are structural. Merger extinguishes an easement when one owner comes to hold both parcels. A recorded release ends it by agreement. Abandonment ends it when the holder's conduct shows an intent to give up the right. And mere non-use, even for decades, usually is not enough on its own.",
      "Two special cases have built-in endings. An easement by necessity generally ends when the necessity does, such as when a new public road reaches the parcel. And term easements, common in conservation programs, cell sites and billboards, genuinely expire on the date written into the document.",
    ],
    whatToLookFor: [
      "Any stated term, condition subsequent, or reversion language in the grant.",
      "Evidence of abandonment: facilities removed, route obliterated, holder disclaiming the right.",
      "Whether the parcels have ever been under common ownership, which may have triggered merger.",
      "A recorded release, without which the exception stays on your title even if the use stopped.",
    ],
    relatedGlossary: ["easement", "encumbrance", "easement-by-necessity", "title-policy-exception"],
    relatedQuestions: ["can-i-remove-an-easement-from-my-property", "what-is-an-easement-by-necessity"],
    category: "Types of easements",
  },
  {
    slug: "what-is-an-easement-in-gross",
    question: "What is an easement in gross?",
    shortAnswer:
      "An easement in gross belongs to a person or a company rather than to a neighboring parcel, which is the category most utility, pipeline, railroad and billboard easements fall into.",
    answer: [
      "Every easement burdens land, but only some of them benefit land. When there is no benefited parcel. When the right belongs to the electric company itself, the easement is in gross.",
      "Commercial easements in gross are freely transferable and are treated as business assets. That is why a corridor granted to a small rural cooperative in the 1960s can be held today by a national utility, without you ever being told.",
      "Personal easements in gross are the opposite: a right granted to a named individual, such as permission to fish a pond, generally cannot be transferred and ends when that person dies. Courts prefer to read ambiguous grants as appurtenant rather than in gross, precisely because rights tied to land are easier to trace.",
    ],
    whatToLookFor: [
      "Whether any benefited parcel is identified anywhere in the grant.",
      "An express right to assign, which signals a commercial easement in gross.",
      "Whether the easement is exclusive, which can bar even your own use of the corridor.",
      "Payment terms, since cell, billboard and fiber agreements often carry recurring rent.",
    ],
    relatedGlossary: ["easement-in-gross", "appurtenant-easement", "utility-easement", "servient-estate"],
    relatedQuestions: ["what-are-the-three-types-of-easements", "do-easements-transfer-with-the-property"],
    category: "Types of easements",
  },
  {
    slug: "do-i-need-a-survey-to-locate-an-easement",
    question: "Do I need a survey to locate an easement?",
    shortAnswer:
      "If you are building, fencing or paving anywhere near the corridor, yes, a recorded description and a plat scaled by eye are not accurate enough to place a structure against.",
    answer: [
      "Recorded easements are described in words: a width, a centerline, a bearing and distance from a monument. Turning that description into stakes on the ground is surveying work, and small errors in reading it become expensive when a slab is already poured.",
      "A boundary and easement survey does three things at once: it locates the parcel lines, it plots every recorded easement from the title commitment, and it shows where physical features actually sit relative to both. That last part is what reveals encroachments before someone else finds them.",
      "You can skip the survey for low-stakes questions. Understanding a title exception, planning shallow landscaping, and our parcel maps are useful for that. They are drawn from free public data and are not a survey, so they orient you rather than authorize construction.",
    ],
    whatToLookFor: [
      "A licensed surveyor in your state, with the title commitment supplied to them in advance.",
      "A scope that plots every Schedule B easement, not just the boundary.",
      "Staking on the ground if you intend to build, rather than a drawing alone.",
      "A comparison note where physical features conflict with the recorded description.",
    ],
    relatedGlossary: ["survey", "encroachment", "schedule-b", "buildable-envelope"],
    relatedQuestions: [
      "how-much-does-it-cost-to-survey-easements",
      "how-to-find-easements-on-my-property",
    ],
    category: "Finding easements",
  },
  {
    slug: "can-i-be-paid-for-an-easement-on-my-land",
    question: "Can I be paid for an easement on my land?",
    shortAnswer:
      "For a new easement, yes, compensation is negotiable and is normally measured by what the corridor takes from your property's value. For an easement that already exists, that payment was made long ago.",
    answer: [
      "When a utility, pipeline or agency asks for a new corridor, you are selling a property right, and the price should reflect the value of what you lose: the strip's use, plus any damage to the rest of the parcel from being split, restricted or made harder to develop.",
      "Appraisers size this with a before-and-after analysis. They value the whole property unencumbered, value it again with the corridor in place, and treat the difference as the compensation figure. Public agencies with condemnation power use the same framework, which is why an offer based on square footage alone often understates the number.",
      "Existing easements are different. If the corridor was granted decades ago or dedicated on the plat when the subdivision was created, the consideration went to whoever owned the land then. What you can still negotiate is a widening, a second line inside the same corridor, or a crossing permit.",
    ],
    whatToLookFor: [
      "Whether the request is for a new easement, a widening, or an amendment to an existing one.",
      "Damages to the remainder, not just the area of the strip itself.",
      "Terms beyond price: restoration, crop and timber loss, access routes, and depth of cover.",
      "Whether the requester holds condemnation power, which changes the negotiating posture.",
    ],
    relatedGlossary: ["easement-in-gross", "encumbrance", "servient-estate", "utility-easement"],
    relatedQuestions: [
      "does-an-easement-lower-property-value",
      "how-do-utility-easements-affect-property-value",
    ],
    category: "Types of easements",
  },
  {
    slug: "should-i-buy-a-house-with-an-easement",
    question: "Should I buy a house with an easement?",
    shortAnswer:
      "Usually yes, most homes carry routine utility strips along a lot line. What decides it is where the corridor sits, how wide it is, and what the holder is allowed to do inside it.",
    answer: [
      "Easements are so common that refusing every property with one would rule out most of the market. The question is never whether an easement exists but whether this particular one interferes with why you want the house. A ten foot telecom strip along the front line is background noise. A fifty foot pipeline corridor across the only flat part of the back yard is a different purchase.",
      "Work through three things during your inspection period: the location relative to the house and the yard you actually plan to use, the width and the rights granted, and whether anything already built sits inside the strip. That last point is the one that bites buyers, because an encroaching deck or garage becomes your problem the day you close.",
      "If the corridor does constrain the property, that is a price conversation rather than an automatic walk-away. Appraisers measure the effect as the difference between the property with and without the burden, and sellers of constrained lots often expect the discussion.",
    ],
    whatToLookFor: [
      "Where the corridor crosses in relation to the house, the yard, and any addition you have in mind.",
      "Whether the seller's improvements sit inside the easement without written consent.",
      "Access rights: who may enter, how often, and whether they must restore the surface.",
      "Any shared driveway or access agreement that binds you to cost sharing after closing.",
    ],
    stateLawNote:
      "Disclosure duties differ by state. Some require a seller to disclose known easements on a statutory form; others rely on the title commitment alone, which makes your own review of Schedule B the safeguard.",
    relatedGlossary: ["title-commitment", "schedule-b", "encroachment", "buildable-envelope"],
    relatedQuestions: [
      "does-an-easement-lower-property-value",
      "how-to-read-a-title-commitment-for-easements",
    ],
    category: "Buying & selling",
  },
  {
    slug: "does-an-easement-affect-my-mortgage-or-title-insurance",
    question: "Does an easement affect my mortgage or title insurance?",
    shortAnswer:
      "Recorded easements are normally listed as exceptions on your title policy, meaning they are not covered. Lenders rarely object unless the corridor touches the house or blocks legal access.",
    answer: [
      "Every recorded easement shows up in Schedule B of the title commitment as an exception. An exception is not a defect; it is the insurer saying it will not pay you for a loss caused by something already on the record and disclosed to you before closing. That is normal and applies to nearly every property.",
      "Lenders care about two narrower questions. Does the property have legal, recorded access to a public road, and does any easement threaten the structure securing the loan? A landlocked parcel with only a handshake access arrangement is a genuine underwriting problem. A side-yard utility strip is not.",
      "Where an improvement encroaches into a corridor, the title company may add an affirmative endorsement, ask for a consent letter from the holder, or require the encroachment be removed. Ask your closer early, because those cures take weeks, not days.",
    ],
    whatToLookFor: [
      "Whether Schedule B lists an access easement proving legal road frontage.",
      "Survey exceptions, which often signal an encroachment the insurer has noticed.",
      "Available endorsements covering encroachment or forced removal.",
      "Whether the lender's appraisal comments on the corridor at all.",
    ],
    relatedGlossary: ["title-policy-exception", "schedule-b", "title-commitment", "encumbrance"],
    relatedQuestions: [
      "how-to-read-a-title-commitment-for-easements",
      "should-i-buy-a-house-with-an-easement",
    ],
    category: "Buying & selling",
  },
  {
    slug: "how-do-i-sell-a-house-with-an-easement",
    question: "How do I sell a house with an easement?",
    shortAnswer:
      "Disclose it early, have the recorded document ready, and be able to say plainly what the corridor allows. Surprises late in escrow cost more than the easement ever does.",
    answer: [
      "Buyers rarely walk away from an easement itself. They walk away from finding out about one three days before closing, because at that point they cannot tell whether it is routine or serious and the safe answer is no. A seller who hands over the recorded grant on day one changes the whole tone of the transaction.",
      "Prepare a short packet: the plat or grant showing the corridor, a note on where it runs, and evidence of any consent the holder has given for improvements near it. If the previous owner built a deck or fence inside the strip, deal with it before listing rather than during the inspection period.",
      "Price accordingly where the burden is real. A corridor that removes a buildable back yard affects value, and an agent who prices as though it does not will spend the discount later in repair credits and delays.",
    ],
    whatToLookFor: [
      "Complete recorded documents, not just a line on the plat.",
      "Any structure of yours sitting inside the corridor without written consent.",
      "Whether a shared driveway agreement transfers cleanly to the buyer.",
      "State disclosure forms that ask specifically about easements and shared access.",
    ],
    relatedGlossary: ["encumbrance", "servient-estate", "title-commitment", "survey"],
    relatedQuestions: [
      "does-an-easement-lower-property-value",
      "what-happens-if-i-build-on-an-easement",
    ],
    category: "Buying & selling",
  },
  {
    slug: "a-utility-company-wants-an-easement-on-my-land",
    question: "A utility company wants an easement on my land. What now?",
    shortAnswer:
      "Treat it as selling a property right, not signing a form. The first offer is an opening position, and the terms usually matter as much as the price.",
    answer: [
      "The document in front of you was drafted by the utility's lawyers to give the utility maximum flexibility: a generous width, the right to add future lines, unrestricted access, and few obligations to restore your land. None of that is fixed. Easement grants are negotiated every day, and the terms in the first draft are the ones most easily improved.",
      "Understand who is asking. A regulated utility or public agency may hold condemnation power, which means it can take the corridor through a legal process if you refuse, paying compensation set by that process. A private developer or telecom without that power needs your agreement, which puts you in a far stronger position.",
      "Get the corridor defined before you sign. A grant that names a fixed width in a fixed location on an attached exhibit is far better for you than a floating or blanket right somewhere on the parcel.",
    ],
    whatToLookFor: [
      "A fixed width and a surveyed location, rather than a blanket right across the parcel.",
      "Whether future additional lines are permitted inside the same corridor.",
      "Restoration duties: topsoil, fencing, drainage, crops, and timber.",
      "Access route and gate rules, so crews do not cross the rest of the property at will.",
      "Whether the grant is permanent or expires if the use ends.",
    ],
    stateLawNote:
      "Whether the requester can condemn depends on state law and the type of entity, so confirm the legal authority claimed in the offer letter before assuming you must agree.",
    relatedGlossary: ["easement-in-gross", "blanket-easement", "utility-easement", "servient-estate"],
    relatedQuestions: [
      "can-i-be-paid-for-an-easement-on-my-land",
      "can-i-refuse-to-grant-an-easement",
    ],
    category: "Utility requests",
  },
  {
    slug: "can-i-refuse-to-grant-an-easement",
    question: "Can I refuse to grant an easement?",
    shortAnswer:
      "Yes, unless the party asking has condemnation power or a court finds an easement by necessity. Refusing a private request is entirely your right.",
    answer: [
      "For an ordinary request from a neighbor, a developer, or a company without statutory authority, no is a complete answer. Nobody can compel you to convey a property right simply because it would be convenient for them.",
      "Two situations change that. Entities with the power of eminent domain, such as many regulated utilities, pipeline operators and public agencies, can acquire a corridor through condemnation if negotiation fails, with compensation determined by that process rather than by you. Separately, a court can find an easement by necessity where a landlocked parcel was severed from yours and has no other legal access.",
      "Refusing is often a negotiating position rather than a final answer. Owners who say no to the first draft frequently end up with a narrower corridor, a better route, stronger restoration terms and a higher payment than owners who sign immediately.",
    ],
    whatToLookFor: [
      "Whether the requester actually holds condemnation power or is only implying it.",
      "Whether the neighboring parcel truly lacks other access.",
      "Alternative routes across your land that would burden you less.",
      "Any deadline in the offer letter, which is usually softer than it reads.",
    ],
    relatedGlossary: ["easement-by-necessity", "servient-estate", "dominant-estate", "encumbrance"],
    relatedQuestions: [
      "a-utility-company-wants-an-easement-on-my-land",
      "what-is-an-easement-by-necessity",
    ],
    category: "Utility requests",
  },
  {
    slug: "can-a-utility-company-cut-down-my-trees",
    seoTitle: "Can a Utility Cut Down Your Trees? Inside vs. Outside the Strip",
    seoDescription:
      "Inside its easement, usually yes, and without paying you. Outside the strip it generally needs your consent or a safety reason. See your rights and what to ask.",
    question: "Can a utility company cut down my trees?",
    shortAnswer:
      "Inside its easement, usually yes, vegetation management is part of the granted right. Outside the corridor, it generally needs your permission or a safety justification.",
    answer: [
      "Most utility grants include an express right to trim, remove and control vegetation that could interfere with the facility. Within the recorded strip, that right is broad, and the utility does not generally owe you compensation for exercising it because the right was granted with the easement.",
      "Beyond the corridor, the picture changes. Utilities often rely on a danger tree provision or on general safety authority to reach a tree outside the strip that could fall into the line. Those removals are more constrained, and many utilities will negotiate, replace, or pay rather than argue.",
      "The practical remedy is usually procedural. Ask for the crew's work order and the clause it relies on, ask what standard sets the clearance distance, and ask for the plan in writing before work starts. Most disputes come from a crew arriving unannounced, not from the underlying right.",
    ],
    whatToLookFor: [
      "The vegetation management wording in your recorded grant.",
      "Whether the tree is inside the strip, and where the strip's edge really is.",
      "The utility's published clearance standard and notification policy.",
      "Whether restoration, chipping or stump removal is offered.",
    ],
    relatedGlossary: ["utility-easement", "right-of-way", "servient-estate", "encumbrance"],
    relatedQuestions: [
      "can-i-plant-trees-in-a-utility-easement",
      "who-owns-the-land-under-an-easement",
    ],
    category: "Utility requests",
  },
  {
    slug: "how-wide-is-a-utility-easement",
    seoTitle: "How Wide Is a Utility Easement? Typical Feet by Type",
    seoDescription:
      "Most home utility easements are 10 to 20 feet wide: about 10 ft for buried lines, 20 ft for overhead power. See widths by utility type and how to confirm yours.",
    question: "How wide is a utility easement?",
    shortAnswer:
      "Ten to twenty feet covers most residential utility strips, with buried service lines commonly at ten feet and overhead distribution at twenty. Transmission and pipeline corridors are far wider.",
    answer: [
      "Width follows the work the holder has to do, not the size of the pipe or wire. A crew needs room to dig, stack spoil, stand beside the trench and bring in equipment, which is why a two inch cable can sit in a ten foot strip and a deep sewer can justify thirty or forty feet.",
      "Utilities draft from standard forms, so the same numbers repeat across the country: ten feet for buried service, fifteen to twenty for gas mains and water mains, twenty for a pole line, twenty for a residential shared driveway, fifty for a transmission pipeline, and a hundred or more for high voltage transmission.",
      "None of that overrides your document. A plat note or a negotiated grant can set any width the parties agreed to, and older grants sometimes state no width at all. The recorded dimension is the one that governs what you can do.",
    ],
    whatToLookFor: [
      "The dimension written on the plat or in the recorded grant, in feet.",
      "Whether the width is centered on the facility or measured from a lot line.",
      "Whether several utilities share one platted strip.",
      "Any temporary construction easement alongside the permanent one.",
    ],
    relatedGlossary: ["utility-easement", "right-of-way", "blanket-easement", "survey"],
    relatedQuestions: [
      "what-is-a-utility-easement-on-my-property",
      "can-i-build-a-shed-over-an-easement",
    ],
    category: "Types of easements",
  },
  {
    slug: "who-is-liable-if-someone-is-hurt-in-an-easement",
    question: "Who is liable if someone is hurt in an easement?",
    shortAnswer:
      "Generally the party whose activity or failure caused the harm. The holder is normally responsible for its own works and crews; the owner remains responsible for the land itself.",
    answer: [
      "Ownership of the strip stays with you, so responsibility does not simply transfer to the easement holder. Courts tend to allocate liability by who controlled the thing that caused the injury: a crew's open trench points to the holder, a rotted retaining wall you built points to you.",
      "Written grants often settle this directly. Many utility and access easements contain an indemnity clause stating that the holder covers claims arising from its use, and sometimes a matching clause running the other way. Read your document before assuming the common law answer applies.",
      "For shared driveways, ask your insurer how the arrangement is treated. Standard homeowner policies usually respond to injuries on your land, but a formal maintenance agreement with cost sharing is worth mentioning at renewal so there is no argument later.",
    ],
    whatToLookFor: [
      "Any indemnity or hold harmless clause in the recorded grant.",
      "Who physically maintains the surface, the gate, or the equipment.",
      "Whether your homeowner policy has been told about a shared access arrangement.",
      "Local rules on premises liability for permitted versus unauthorized entrants.",
    ],
    stateLawNote:
      "Premises liability is state law and varies considerably, particularly on the duty owed to recreational users and to people entering under a recorded right.",
    relatedGlossary: ["servient-estate", "dominant-estate", "right-of-way", "encumbrance"],
    relatedQuestions: [
      "who-maintains-a-shared-driveway",
      "who-owns-the-land-under-an-easement",
    ],
    category: "Disputes & neighbors",
  },
  {
    slug: "does-an-easement-affect-property-taxes",
    question: "Does an easement affect my property taxes?",
    shortAnswer:
      "Sometimes. You still own and are taxed on the land, but assessors may reduce the assessed value where a corridor materially limits how the parcel can be used.",
    answer: [
      "The land under an easement remains yours and stays on your tax roll. What can change is the value the assessor assigns to it, because assessment is meant to reflect market value and a burdened parcel can be worth less than an unburdened one.",
      "Whether you see a reduction depends on the assessor's practice and on how severe the burden is. A narrow utility strip along a lot line rarely moves an assessment. A wide corridor that removes the buildable area, or a conservation easement that permanently restricts development, often does, and conservation easements in particular have their own valuation and, in some states, tax treatment.",
      "If you believe the burden is not reflected, the route is the ordinary appeal process: file within your jurisdiction's window with the recorded document, a map showing the corridor, and evidence of the restriction's effect on use.",
    ],
    whatToLookFor: [
      "How your assessor's records describe the parcel's usable area.",
      "The appeal deadline in your county, which is often short.",
      "Whether comparable burdened parcels nearby are assessed lower.",
      "Any state program giving special treatment to conservation restrictions.",
    ],
    stateLawNote:
      "Assessment rules, appeal windows and conservation easement tax treatment are set by state and local law, so check your county assessor's published guidance.",
    relatedGlossary: ["conservation-easement", "encumbrance", "buildable-envelope", "servient-estate"],
    relatedQuestions: [
      "does-an-easement-lower-property-value",
      "what-is-a-conservation-easement",
    ],
    category: "Buying & selling",
  },
  {
    slug: "how-do-i-get-an-easement-on-someone-elses-property",
    question: "How do I get an easement on someone else's property?",
    shortAnswer:
      "Negotiate written terms with the owner, have a surveyor describe the exact strip, sign a grant that meets your state's formalities, and record it with the county.",
    answer: [
      "An easement is a property right, so getting one works much like buying a small slice of use rather than asking a favor. You approach the owner of the land you need to cross, agree on what the right covers and what it is worth, then put it in a written grant that a stranger reading it in twenty years could follow.",
      "The description does the real work. A grant that says 'a driveway across the north side' invites a fight; a grant with a surveyed centerline, a stated width in feet, and a legal description tied to the deed does not. Most surveyors prepare an easement exhibit for a modest fee, and lenders and title companies expect one.",
      "Recording is what makes the right survive a sale. An unrecorded easement can bind the two people who signed it and still be worthless against the next buyer, so the signed grant goes to the county recorder, clerk, or register of deeds where the burdened land sits.",
      "If the owner will not deal and your parcel has no legal access at all, the remaining route is a court action for an easement by necessity or, in some states, a statutory private way. That is a lawsuit, not a form, and it is slower and costlier than paying for a voluntary grant.",
    ],
    whatToLookFor: [
      "A surveyed exhibit with the width in feet and a legal description tied to the deed.",
      "The stated purpose: access only, or utilities and drainage too.",
      "Who maintains the surface and who pays for repairs.",
      "Whether the grant is permanent and runs with the land, or personal to you.",
      "Any lender consent needed if the burdened parcel carries a mortgage.",
    ],
    stateLawNote:
      "Signing, witnessing and notarization rules for a recorded grant are set by state law, and the recording office is county level, so confirm both before you sign.",
    relatedGlossary: ["easement", "appurtenant-easement", "easement-by-necessity", "right-of-way", "survey"],
    relatedQuestions: [
      "how-do-i-grant-an-easement-on-my-property",
      "what-does-it-cost-to-record-an-easement",
      "what-is-an-easement-by-necessity",
    ],
    category: "Getting an easement",
  },
  {
    slug: "how-do-i-grant-an-easement-on-my-property",
    question: "How do I grant an easement on my property?",
    shortAnswer:
      "Decide exactly what you are giving up, write it narrowly with a surveyed description, sign before a notary, and record it. Terms you leave out are terms you cannot enforce later.",
    answer: [
      "Granting an easement is permanent in most cases, so the drafting matters more than the negotiation. You are handing someone a right to use part of your land that will outlast you and bind whoever buys the property next.",
      "Keep the grant narrow. Name the purpose, fix the location by survey rather than by description of current use, state the width in feet, and say what the holder may not do: no widening, no additional lines, no assignment to a third party without your consent. A blanket grant that covers the whole parcel is the single most common drafting mistake.",
      "Address maintenance, restoration after digging, insurance, and what happens if the use stops. Silence defaults to whatever your state's case law says, which is rarely what you assumed.",
      "If your property is mortgaged, check the loan documents. Many lenders require written consent before an owner encumbers the land, and recording without it can technically breach the loan.",
    ],
    whatToLookFor: [
      "A defined strip by survey, never a blanket right over the whole parcel.",
      "A purpose clause limited to the specific use being granted.",
      "Restoration language covering grade, landscaping and paving after any work.",
      "Whether the right can be assigned, expanded, or shared with other utilities.",
      "Termination or reversion language if the use is abandoned.",
    ],
    stateLawNote:
      "Formalities for a valid conveyance, including notarization and witness requirements, are state specific, and some states require the grant to be signed by both parties.",
    relatedGlossary: ["easement", "servient-estate", "blanket-easement", "encumbrance", "right-of-way"],
    relatedQuestions: [
      "how-do-i-get-an-easement-on-someone-elses-property",
      "can-i-be-paid-for-an-easement-on-my-land",
      "can-i-refuse-to-grant-an-easement",
    ],
    category: "Getting an easement",
  },
  {
    slug: "what-does-it-cost-to-record-an-easement",
    question: "What does it cost to record an easement?",
    shortAnswer:
      "Recording itself is usually the cheapest part, a per-document or per-page county fee. The survey, drafting and any payment for the right are what actually cost money.",
    answer: [
      "County recording fees are set by statute or by the recording office, and are charged either as a flat amount per document or as a base amount plus a per-page charge. Because those schedules change, the office's own published fee page is the only figure worth relying on, and every county guide on this site links to it directly.",
      "The larger costs sit around the filing. A surveyor preparing an easement exhibit and legal description is typically the biggest line item, followed by whoever drafts the grant. If the burdened owner is being paid for the right, that negotiated amount dwarfs both.",
      "Budget for the extras that catch people out: notary fees, certified copies, any state or county documentary charge that applies to conveyances, and a title update if a lender needs to see the recorded instrument.",
      "One cost you avoid by recording is far larger. An unrecorded grant may not bind a future buyer of the burdened land, and re-negotiating a lost right with a new owner is usually far more expensive than the original filing.",
    ],
    whatToLookFor: [
      "Whether your county charges per document or per page.",
      "Formatting rules: margin, font size and first-page requirements cause rejections.",
      "Whether a documentary or transfer charge applies to easement grants in your state.",
      "How many certified copies you need for the lender or title company.",
    ],
    stateLawNote:
      "Recording fees and any documentary charges are set at state or county level, so confirm the current schedule with the office where the land sits.",
    relatedGlossary: ["easement", "encumbrance", "title-commitment", "survey"],
    relatedQuestions: [
      "how-do-i-grant-an-easement-on-my-property",
      "how-much-does-it-cost-to-survey-easements",
      "how-do-i-get-an-easement-on-someone-elses-property",
    ],
    category: "Getting an easement",
  },
  {
    slug: "utility-corridor-requirements",
    question: "What are the requirements inside a utility corridor?",
    shortAnswer:
      "A corridor keeps a continuous, clear working area: no permanent structures, limited planting, no grade changes, and unobstructed access for crews and equipment at any time.",
    answer: [
      "A utility corridor is wider and stricter than a single service easement. It carries transmission or trunk facilities across many parcels, so the operator manages it as one continuous route rather than as a series of backyard strips. What the corridor requires on your lot is usually driven by the operator's system-wide standards, not by anything specific to your property.",
      "The core requirement is a permanently clear working area. Permanent structures are prohibited across the corridor: houses, garages, decks, pools, retaining walls, and usually sheds on footings. Fences are often allowed to cross only where a gate lets equipment through, and the operator normally holds the key or the code.",
      "Vegetation is managed on a cycle. Tall-growing trees are removed inside the corridor and often within a side buffer, and the operator can return to cut without asking each time. Low shrubs, grass, gardens and driveways crossing at an angle are commonly permitted.",
      "Grade changes matter as much as structures. Adding fill over a buried line reduces cover for excavation and can violate the grant; cutting grade exposes it. Any change in the surface elevation inside a corridor should be cleared with the operator first.",
      "Two documents control, and they are not the same. The recorded grant sets the legal width and purpose; the operator's encroachment standards set what it will approve in practice. Ask for both, in writing, before you plan anything inside the strip.",
    ],
    whatToLookFor: [
      "The recorded width in feet and whether it is measured from a centerline or a boundary.",
      "Whether the grant is for named facilities or open-ended 'utility purposes'.",
      "Vegetation-management rights, including any side buffer beyond the recorded width.",
      "Access rights: which side crews enter from, and gate or key requirements.",
      "The operator's published encroachment or crossing-permit process.",
      "Depth of cover for buried lines, and any prohibition on adding fill.",
    ],
    stateLawNote:
      "Call 811 before any digging inside or near a corridor; marking is required by law in every state and it is free.",
    relatedGlossary: ["utility-easement", "blanket-easement", "right-of-way", "encroachment", "setback"],
    relatedQuestions: [
      "how-wide-is-a-utility-easement",
      "can-i-plant-trees-in-a-utility-easement",
      "what-happens-if-i-build-on-an-easement",
    ],
    category: "Utility requests",
  },
];

export const HOMEOWNER_QUESTIONS: HomeownerQuestion[] = [...BASE_QUESTIONS, ...PAA_QUESTIONS];

export function findHomeownerQuestion(slug: string): HomeownerQuestion | undefined {
  return HOMEOWNER_QUESTIONS.find((q) => q.slug === slug);
}

export function getQuestionCategories(): QuestionCategory[] {
  return QUESTION_CATEGORIES;
}

export function getQuestionsByCategory(category: QuestionCategory): HomeownerQuestion[] {
  return HOMEOWNER_QUESTIONS.filter((q) => q.category === category);
}

export function getRelatedQuestions(question: HomeownerQuestion): HomeownerQuestion[] {
  return question.relatedQuestions
    .map((slug) => findHomeownerQuestion(slug))
    .filter((q): q is HomeownerQuestion => Boolean(q));
}
